import { useState, useEffect, useRef } from 'react';

export interface Column {
  id: string;
  label: string;
  visible: boolean;
  required?: boolean; // If true, column cannot be hidden
}

interface ColumnCustomizerProps {
  columns: Column[];
  onColumnsChange: (columns: Column[]) => void;
  storageKey?: string; // For persisting to localStorage
}

export default function ColumnCustomizer({ columns, onColumnsChange, storageKey }: ColumnCustomizerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Load saved column visibility from localStorage
  useEffect(() => {
    if (storageKey) {
      const saved = localStorage.getItem(`columnVisibility_${storageKey}`);
      if (saved) {
        try {
          const savedVisibility = JSON.parse(saved);
          const updatedColumns = columns.map(col => ({
            ...col,
            visible: savedVisibility[col.id] ?? col.visible
          }));
          onColumnsChange(updatedColumns);
        } catch (e) {
          console.error('Failed to parse saved column visibility:', e);
        }
      }
    }
  }, [storageKey]);

  // Save to localStorage when columns change
  const handleColumnToggle = (columnId: string) => {
    const updatedColumns = columns.map(col => 
      col.id === columnId && !col.required 
        ? { ...col, visible: !col.visible }
        : col
    );
    onColumnsChange(updatedColumns);
    
    if (storageKey) {
      const visibility: Record<string, boolean> = {};
      updatedColumns.forEach(col => {
        visibility[col.id] = col.visible;
      });
      localStorage.setItem(`columnVisibility_${storageKey}`, JSON.stringify(visibility));
    }
  };

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={modalRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        title="Customize columns"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 min-w-[200px] py-2">
          <div className="px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 mb-1">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Show/Hide Columns</span>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {columns.map(column => (
              <label
                key={column.id}
                className={`flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer ${column.required ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={column.visible}
                  onChange={() => handleColumnToggle(column.id)}
                  disabled={column.required}
                  className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700"
                />
                <span className="text-xs text-gray-700 dark:text-gray-300">{column.label}</span>
                {column.required && (
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">(required)</span>
                )}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
